// Le module « Formation » (D-141, bêta club pilote ; maquettes f1 à f10 validées) : ce qui ne dépend que des données.
// Les parcours communs définis par Arceau (les mêmes que la base, _formation_steps : testé), leurs textes, les simulateurs
// et leurs adresses, les seuils par défaut, ce que dit chaque étape, la validité (jusqu'au 30 juin), les lignes des
// référents et du bureau. Logique pure, testée (formation.test.ts). Tutoiement ; pas d'écriture inclusive (D-008).

export type ParcoursKey = 'otm' | 'arb' | 'arb2';
export const PARCOURS_KEYS: ParcoursKey[] = ['otm', 'arb', 'arb2'];
export type StepKind = 'doc' | 'support' | 'sim' | 'quiz' | 'practice';
export type SimKey = 'table' | 'gestes' | 'placement';

/** Un simulateur (prototype servi par l'app, public/formation/) : son fichier, son paramètre de mode, ce qu'il dure. */
export interface SimInfo {
  sim: SimKey;
  file: string;
  /** Le paramètre d'adresse qui fixe le mode (`mode=pas`, `vue=tab`). */
  param: 'mode' | 'vue';
  value: string;
  /** « Pas à pas » (le bandeau du plein écran). */
  short: string;
  /** Les faits de la fiche (f5a) : « 24 annonces », « 8 min environ ». */
  count: string;
  minutes: number;
  /** Ce que montre l'image de la fiche. */
  frame: string;
  /** La description longue (la fiche avant « Lancer »). */
  about: string;
  /** Sous le score, sur la fiche. */
  hint: string;
  thumb: string;
}

export interface StepDef {
  key: string;
  kind: Exclude<StepKind, 'support'>;
  /** Le seuil par défaut (D-141) : % pour un simulateur, bonnes réponses sur 10 pour le quiz, matchs pour la pratique. */
  threshold?: number;
  /** Le petit titre au-dessus de l'étape (« Simulateur · table de marque »). */
  kicker: string;
  /** D-145 : l'étape se valide avec le son (« comme en match » : l'annonce est à l'oreille) ; sans voix française sur
   *  l'appareil, le texte reste et l'essai compte. */
  sound?: boolean;
  title: (need: number) => string;
  /** Une phrase sous le titre. */
  sub?: (need: number) => string;
  sim?: SimInfo;
}

/** Un document officiel (toujours un lien, jamais recopié) ou les fiches d'Arceau. */
export interface DocInfo { title: string; sub: string; url: string; badge: 'PDF' | 'FICHES' }

export interface ParcoursInfo {
  key: ParcoursKey;
  /** « OTM ★ », « Arbitre club ★ » : le badge. */
  name: string;
  /** « Parcours OTM ». */
  title: string;
  /** Le picto de l'écusson. */
  seal: 'otm' | 'arb';
  /** L'intro de la page du parcours. */
  lead: string;
  /** Le parcours du bénévolat qui le nourrit (D-100) : l'écusson se pose à côté de son grade. */
  badgeParcours: 'table' | 'arbitrage';
  /** Le badge, en entier (« OTM ★ · officielle de table du club »), accordé plus tard par l'écran. */
  prize: string;
  /** ★★ : bêta dans la bêta, après ★. */
  beta?: boolean;
  requires?: ParcoursKey;
  /** L'étape « document » : les fiches d'Arceau (OTM) ou le document FFBB, et « pour aller plus loin ». */
  doc: { kicker: string; title: string; main: DocInfo; more?: DocInfo };
  steps: StepDef[];
}

export const FFBB = {
  emarque: 'https://files.ffbb.com/sites/default/files/manuel_de_lutilisateur_e-marque_v2_-2020.pdf',
  signaux: 'https://files.ffbb.com/sites/default/files/otm_regelement_jeu/9a._signaux_officiels_des_arbitres_2020_-_bvr_.pdf',
  iot: 'https://files.ffbb.com/sites/default/files/otm_regelement_jeu/1._manuel_iot_-_fiba_-_technique_darbitrage_v.1.1a_nov2020_fr_1.pdf',
};
/** Les pages servies par l'app (même origine), copiées par scripts/formation/copy-sims.mjs. */
export const FILES = { table: '/formation/table-de-marque.html', gestes: '/formation/gestes-arbitrage.html', placement: '/formation/placement-arbitre.html',
  fiches: '/formation/fiches-otm.html' } as const;
const IMG = (f: string) => `/formation/img/${f}`;

/** Les trois étapes de la table (D-145) : l'annonce écrite pas à pas, puis l'arbitre (gestes et voix) avec le texte, puis
 *  comme en match (gestes et voix sans texte, au chrono). Les modes du simulateur : pas, m1, m3. */
export type TableMode = 'pas' | 'm1' | 'm3';
const TABLE: Record<TableMode, Pick<SimInfo, 'short' | 'minutes' | 'frame' | 'about' | 'hint' | 'thumb'>> = {
  pas: { short: 'Pas à pas', minutes: 8, frame: 'Écran « Match », comme l’e-Marque V2', thumb: IMG('table.jpg'),
    about: 'L’annonce de l’arbitre est écrite (panier, faute, temps-mort, remise en jeu) ; tu saisis comme sur la feuille électronique. Une fin de match serrée, 4e période : un nouveau match à chaque essai (clubs, joueuses, score, fautes, temps morts).',
    hint: 'Seul ton meilleur essai compte : recommence autant que tu veux. L’aide est là en pas à pas.' },
  m1: { short: 'Avec l’arbitre', minutes: 10, frame: 'L’arbitre dessiné pour Arceau, ses gestes et sa voix', thumb: IMG('table-arbitre.jpg'),
    about: 'Les mêmes sortes d’annonces, faites par l’arbitre : ses gestes officiels et sa voix (le son de ton téléphone), le texte encore affiché dessous. À ton rythme, l’aide à portée.',
    hint: 'Seul ton meilleur essai compte : recommence autant que tu veux. L’aide reste là, comme en pas à pas.' },
  m3: { short: 'Comme en match', minutes: 8, frame: 'Gestes et voix de l’arbitre, sans texte, au chrono', thumb: IMG('table-arbitre.jpg'),
    about: 'Comme un soir de match : l’arbitre fait ses gestes et parle, sans le texte de l’annonce. Le temps de saisie part à la fin de sa séquence.',
    hint: 'Seul ton meilleur essai compte : recommence autant que tu veux. Une annonce pas saisie à temps compte comme une erreur.' },
};
const tableSim = (value: TableMode): SimInfo => ({ sim: 'table', file: FILES.table, param: 'mode', value, count: '24 annonces', ...TABLE[value] });
/** Le mode a la voix de l'arbitre (D-145) : la fiche dit d'activer le son, le pont le propose à l'ouverture. */
export const simHasVoice = (s: Pick<SimInfo, 'sim' | 'value'>): boolean => s.sim === 'table' && /^[vm][1-3]$/.test(s.value);
const gestesSim = (value: 'rec' | 'num' | 'tab'): SimInfo => ({
  sim: 'gestes', file: FILES.gestes, param: 'vue', value,
  short: value === 'rec' ? 'Reconnaître' : value === 'num' ? 'Lire un numéro' : 'Compte rendu',
  count: value === 'tab' ? '10 fautes' : value === 'num' ? '15 numéros' : '15 gestes', minutes: value === 'tab' ? 6 : 4,
  frame: 'Silhouettes dessinées pour Arceau, d’après l’annexe A', thumb: IMG(value === 'rec' ? 'geste.jpg' : value === 'num' ? 'numero.jpg' : 'compte-rendu.jpg'),
  about: value === 'rec' ? 'Un arbitre fait un geste : choisis son nom parmi 4 propositions, souvent de la même famille. La correction vient à chaque réponse.'
    : value === 'num' ? 'L’arbitre montre un numéro avec ses mains (les dizaines de dos, puis les unités de face) : tu le lis et tu le tapes.'
    : 'L’arbitre arrête le jeu, montre le numéro, la faute, la sanction : tu notes la faute sur le pavé, comme à la table. Des fautes et des numéros nouveaux à chaque série.',
  hint: 'Seul ton meilleur essai compte : recommence autant que tu veux. Le catalogue des signaux reste ouvert dans le document.',
});
const placementSim = (value: 'pl' | 'ord' | 'qz'): SimInfo => ({
  sim: 'placement', file: FILES.placement, param: 'vue', value,
  short: value === 'pl' ? 'Où te places-tu ?' : value === 'ord' ? 'Dans l’ordre' : 'Les principes',
  count: value === 'pl' ? '12 situations' : value === 'ord' ? '6 procédures' : '12 questions', minutes: value === 'pl' ? 8 : 5,
  frame: 'Le terrain vu de dessus, dessiné pour Arceau', thumb: IMG('placement.jpg'),
  about: value === 'pl' ? 'Une action se joue puis se fige au contact ou au tir : place-toi, en arbitre de tête ou de queue. La correction est calculée (ligne de vue, angle, distance) ; les joueuses et leurs numéros changent à chaque essai.'
    : value === 'ord' ? 'Entre-deux, remise en jeu, temps-mort, fautes : remets dans l’ordre les étapes de 6 procédures, tirées parmi 12, et qui fait quoi.'
    : 'Les principes du placement à deux, tous chapitres mélangés, d’après le manuel IOT.',
  hint: 'Seul ton meilleur essai compte : recommence autant que tu veux.',
});
const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

export const PARCOURS: Record<ParcoursKey, ParcoursInfo> = {
  otm: {
    key: 'otm', name: 'OTM ★', title: 'Parcours OTM', seal: 'otm', badgeParcours: 'table',
    lead: 'Officiel de table de marque : tenir l’e-Marque, le chronomètre et les 24 secondes d’un match.',
    prize: 'officiel de table du club',
    doc: {
      kicker: 'Fiches · Arceau', title: 'Lire les fiches de la table',
      main: { title: 'Les fiches de la table de marque', sub: 'Arceau · 12 gestes, une fiche et un test chacun', url: FILES.fiches, badge: 'FICHES' },
      more: { title: 'Manuel de l’utilisateur e-Marque V2', sub: 'FFBB · 2020 · PDF sur files.ffbb.com', url: FFBB.emarque, badge: 'PDF' },
    },
    steps: [
      { key: 'table_pas', kind: 'sim', threshold: 80, kicker: 'Simulateur · table de marque', title: () => 'Pas à pas',
        sub: () => 'L’annonce est écrite, tu saisis comme sur l’e-Marque. 24 annonces, un nouveau match à chaque essai, l’aide reste là.', sim: tableSim('pas') },
      // D-145 : l'arbitre et sa voix, le texte encore affiché (mode m1).
      { key: 'table_arbitre', kind: 'sim', threshold: 80, kicker: 'Simulateur · table de marque', title: () => 'Avec l’arbitre',
        sub: () => 'L’arbitre fait ses gestes et parle, le texte encore affiché. Active le son.', sim: tableSim('m1') },
      // D-145 : « comme en match » (mode m3) garde la clé de l'ancienne étape « chrono » (ses essais et son seuil la suivent).
      { key: 'table_chrono', kind: 'sim', threshold: 70, sound: true, kicker: 'Simulateur · table de marque', title: () => 'Comme en match',
        sub: () => 'Gestes et voix sans le texte, au chrono : comme à la table un soir de match.', sim: tableSim('m3') },
      { key: 'quiz', kind: 'quiz', threshold: 8, kicker: 'Quiz', title: () => 'Le quiz de l’e-Marque',
        sub: n => `10 questions tirées du manuel (la page est citée) : ${n} bonnes réponses pour réussir.` },
      { key: 'pratique', kind: 'practice', threshold: 3, kicker: 'Pratique · tes créneaux', title: n => `Tenir la table sur ${plural(n, 'match', 'matchs')}` },
    ],
  },
  arb: {
    key: 'arb', name: 'Arbitre club ★', title: 'Parcours Arbitre club', seal: 'arb', badgeParcours: 'arbitrage',
    lead: 'Arbitrer les matchs de jeunes du club : les gestes officiels, les numéros, le compte rendu à la table.',
    prize: 'arbitre club',
    doc: {
      kicker: 'Document · FFBB', title: 'Les signaux officiels des arbitres',
      main: { title: 'Règlement officiel · annexe A : signaux des arbitres', sub: 'FFBB · 2020 · PDF sur files.ffbb.com', url: FFBB.signaux, badge: 'PDF' },
    },
    steps: [
      { key: 'gestes_rec', kind: 'sim', threshold: 90, kicker: 'Simulateur des gestes', title: () => 'Reconnaître un geste',
        sub: () => 'Un geste s’affiche, tu dis ce qu’il veut dire.', sim: gestesSim('rec') },
      { key: 'gestes_num', kind: 'sim', threshold: 80, kicker: 'Simulateur des gestes', title: () => 'Lire un numéro',
        sub: () => 'L’arbitre montre un numéro avec ses mains, tu le lis.', sim: gestesSim('num') },
      { key: 'gestes_tab', kind: 'sim', threshold: 80, kicker: 'Simulateur des gestes', title: () => 'Le compte rendu à la table',
        sub: () => 'Faute, numéro, lancers francs : tu fais le compte rendu à la table, dans l’ordre.', sim: gestesSim('tab') },
      { key: 'pratique', kind: 'practice', threshold: 3, kicker: 'Pratique · tes créneaux', title: n => `Arbitrer ${plural(n, 'match', 'matchs')} de jeunes` },
    ],
  },
  arb2: {
    key: 'arb2', name: 'Arbitre club ★★', title: 'Parcours Arbitre club ★★', seal: 'arb', badgeParcours: 'arbitrage', beta: true, requires: 'arb',
    lead: 'Le placement des arbitres à deux : où se tenir, en tête ou en queue, et les procédures dans l’ordre.',
    prize: 'arbitre club, le placement',
    doc: {
      kicker: 'Document · FIBA, diffusé par la FFBB', title: 'Le manuel des techniques d’arbitrage',
      main: { title: 'Manuel pour arbitres FIBA · techniques individuelles (IOT)', sub: 'Version française · v1.1a, novembre 2020 · PDF sur files.ffbb.com', url: FFBB.iot, badge: 'PDF' },
    },
    steps: [
      { key: 'place_pl', kind: 'sim', threshold: 80, kicker: 'Simulateur du placement', title: () => 'Où te places-tu ?',
        sub: () => 'Une action se fige au contact ou au tir : place-toi, en tête ou en queue.', sim: placementSim('pl') },
      { key: 'place_ord', kind: 'sim', threshold: 80, kicker: 'Simulateur du placement', title: () => 'Dans l’ordre',
        sub: () => 'Entre-deux, remise en jeu, lancers francs : les étapes de 6 procédures.', sim: placementSim('ord') },
      { key: 'place_qz', kind: 'sim', threshold: 80, kicker: 'Simulateur du placement', title: () => 'Les principes',
        sub: () => 'Le quiz des principes du placement, tous chapitres mélangés.', sim: placementSim('qz') },
      { key: 'pratique', kind: 'practice', threshold: 5, kicker: 'Pratique · tes créneaux', title: n => `Arbitrer ${plural(n, 'match', 'matchs')} de jeunes` },
    ],
  },
};

/** Les étapes d'Arceau d'un parcours, dans l'ordre de la base (le document d'abord). */
export const baseSteps = (p: ParcoursKey): { key: string; kind: Exclude<StepKind, 'support'>; threshold: number | null }[] =>
  [{ key: 'doc', kind: 'doc' as const, threshold: null }, ...PARCOURS[p].steps.map(s => ({ key: s.key, kind: s.kind, threshold: s.threshold ?? null }))];
export const stepDef = (p: ParcoursKey, key: string): StepDef | undefined => PARCOURS[p].steps.find(s => s.key === key);

/** L'adresse d'un simulateur dans l'app, le mode fixé par l'étape. */
export const simUrl = (s: SimInfo): string => `${s.file}?${s.param}=${encodeURIComponent(s.value)}`;

/** « S'entraîner librement » (D-145) : le simulateur complet d'un parcours, tous ses modes et sources, sans note (le pont
 *  n'envoie aucun score). */
export interface FreeSim { sim: SimKey; title: string; sub: string; thumb: string }
export const FREE_SIM: Record<ParcoursKey, FreeSim> = {
  otm: { sim: 'table', title: 'Le simulateur de table', sub: 'Annonce écrite, gestes, voix, gestes et voix : tous les niveaux', thumb: IMG('table-arbitre.jpg') },
  arb: { sim: 'gestes', title: 'Le simulateur des gestes', sub: 'Le catalogue des 65 signaux, reconnaître, lire un numéro, le compte rendu', thumb: IMG('geste.jpg') },
  arb2: { sim: 'placement', title: 'Le simulateur du placement', sub: 'Les situations, les procédures, les principes, à ton rythme ou au chrono', thumb: IMG('placement.jpg') },
};
export const freeUrl = (sim: SimKey): string => `${FILES[sim]}?libre=1`;

/** D-145 enrichi (02/11) : la clé de la personne pour le tirage des simulateurs (public/formation/tirage.js) : courte et non
 *  réversible (FNV-1a, base 36), jamais l'identifiant du compte. L'historique des questions vues reste dans le navigateur, par
 *  personne : sur une tablette du club, chacune garde le sien. */
export function personKey(uid: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < uid.length; i++) { h ^= uid.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(36);
}
/** L'adresse d'un simulateur ou des fiches, avec la clé de la personne (`p=`). */
export const withPerson = (url: string, uid: string | null | undefined): string =>
  (uid ? `${url}${url.includes('?') ? '&' : '?'}p=${personKey(uid)}` : url);

/** L'étape conseillée avant (la table progresse vers le vrai match, D-145) et ce qu'on en dit tant qu'elle n'est pas faite. */
export const ADVISED_AFTER: Record<string, { step: string; text: string }> = {
  table_arbitre: { step: 'table_pas', text: 'conseillé après le pas à pas' },
  table_chrono: { step: 'table_arbitre', text: 'conseillé après «\u00a0Avec l’arbitre\u00a0»' },
};
/** Une étape faite par une autre (D-145 : « comme en match » réussi vaut aussi « avec l'arbitre ») : ce qu'on en dit. */
export function creditLine(p: ParcoursKey, s: Pick<StepState, 'credit'>): string | null {
  if (!s.credit) return null;
  const d = stepDef(p, s.credit);
  return `Validée par «\u00a0${d ? d.title(0) : s.credit}\u00a0» réussi`;
}

// --- La progression (lue en base : formation_state) ---------------------------------------------------------------
export interface StepState {
  step: string;
  kind: StepKind;
  threshold: number | null;
  done: boolean;
  at: string | null;
  opened_at?: string | null;
  how?: string | null;
  support?: string | null;
  best?: number | null;
  tries?: number;
  last_at?: string | null;
  /** D-145 : l'étape est faite par une autre réussie (« avec l'arbitre » par « comme en match » : 'table_chrono'). */
  credit?: string | null;
  score?: number | null;
  total?: number | null;
  count?: number;
  matches?: { day: string; label: string; title: string }[];
}
export interface CertState { origin: 'valide' | 'referent' | 'declare'; by: string | null; at: string; valid_until: string; word: string | null; renewed_at: string | null; status: 'valide' | 'a_renouveler' }
export interface ReviewState { verdict: 'ok' | 'pas_encore'; word: string | null; by: string; at: string }
export interface ParcoursState {
  steps: StepState[];
  done: number;
  total: number;
  complete: boolean;
  started_at: string | null;
  cert: CertState | null;
  review: ReviewState | null;
}

/** Les segments de la barre d'un parcours (accueil, référent, parent) : fait, en cours (la prochaine), à faire. */
export function dotStates(p: Pick<ParcoursState, 'steps'>): ('on' | 'cur' | '')[] {
  const cur = nextStep(p);
  return p.steps.map(s => (s.done ? 'on' : s === cur ? 'cur' : ''));
}
/** La prochaine étape à faire : la première pas faite, la pratique en dernier (elle avance toute seule). */
export function nextStep(p: Pick<ParcoursState, 'steps'>): StepState | null {
  return p.steps.find(s => !s.done && s.kind !== 'practice') ?? p.steps.find(s => !s.done) ?? null;
}
/** Le nom d'une étape (« Pas à pas », « Le quiz de l'e-Marque », un support : son titre). */
export function stepTitle(p: ParcoursKey, s: Pick<StepState, 'step' | 'kind' | 'threshold'>, supportTitle?: string): string {
  if (s.kind === 'doc') return PARCOURS[p].doc.title;
  if (s.kind === 'support') return supportTitle ?? 'Le support du club';
  const d = stepDef(p, s.step);
  return d ? d.title(s.threshold ?? d.threshold ?? 0) : s.step;
}
/** Le nom court d'une étape dans une ligne (référent, bureau, parent) : « pas à pas », « le compte rendu ». */
export function stepShort(p: ParcoursKey, s: Pick<StepState, 'step' | 'kind'>): string {
  if (s.kind === 'doc') return p === 'otm' ? 'les fiches' : 'le document';
  if (s.kind === 'support') return 'la vidéo';
  if (s.kind === 'quiz') return 'le quiz';
  if (s.kind === 'practice') return 'les matchs';
  return SIM_LINE[s.step] ?? s.step;
}
/** Le nom d'un exercice de simulateur dans une phrase (« Ensuite : le chrono et le quiz »). */
const SIM_LINE: Record<string, string> = {
  table_pas: 'le pas à pas', table_arbitre: 'avec l’arbitre', table_chrono: 'comme en match', gestes_rec: 'reconnaître un geste', gestes_num: 'lire un numéro', gestes_tab: 'le compte rendu',
  place_pl: 'le placement', place_ord: 'les procédures', place_qz: 'les principes',
};
/** « 72 % », « 9/10 » ; une étape faite par une autre (D-145) : « ✓ ». */
export function scoreLabel(s: Pick<StepState, 'kind' | 'best' | 'score' | 'total'> & { credit?: string | null }): string {
  if (s.credit) return '✓';
  if (s.kind === 'quiz') return s.score != null ? `${s.score}/${s.total ?? 10}` : '';
  return s.best != null ? `${s.best} %` : '';
}
/** « il faut 80 % », « il faut 8 sur 10 », « il en faut 3 ». */
export function needLabel(s: Pick<StepState, 'kind' | 'threshold'>, past = false): string {
  const t = s.threshold ?? 0;
  const f = past ? 'il fallait' : 'il faut';
  if (s.kind === 'quiz') return `${f} ${t} sur 10`;
  if (s.kind === 'practice') return past ? `il en fallait ${t}` : `il en faut ${t}`;
  return `${f} ${t} %`;
}

/** « 3 étapes sur 7 ». */
export const stepsLine = (done: number, total: number) => `${done} ${done > 1 ? 'étapes' : 'étape'} sur ${total}`;

/** Ce qui reste, en une ligne (haut du parcours, accueil) : « Encore : 2 simulateurs, le quiz ». */
export function remainingLine(p: ParcoursKey, st: Pick<ParcoursState, 'steps'>): string {
  const left = st.steps.filter(s => !s.done);
  if (!left.length) return 'Tout est fait';
  const sims = left.filter(s => s.kind === 'sim').length;
  const parts: string[] = [];
  if (left.some(s => s.kind === 'doc')) parts.push(p === 'otm' ? 'les fiches' : 'le document');
  if (left.some(s => s.kind === 'support')) parts.push('la vidéo');
  if (sims === 1) parts.push(stepShort(p, left.find(s => s.kind === 'sim')!));
  else if (sims > 1) parts.push(`${sims} simulateurs`);
  if (left.some(s => s.kind === 'quiz')) parts.push('le quiz');
  const prac = left.find(s => s.kind === 'practice');
  if (prac) { const n = Math.max(0, (prac.threshold ?? 0) - (prac.count ?? 0)); parts.push(n > 1 ? `${n} matchs` : 'un match'); }
  return `Encore : ${parts.join(', ')}`;
}

/** Les pastilles d'un dossier (« À valider », f6) : « Documents ✓ », « Pas à pas 92 % », « Quiz 9/10 », « 5 matchs à la table ». */
export function dossierChips(p: ParcoursKey, st: Pick<ParcoursState, 'steps'>): { text: string; ok: boolean }[] {
  const out: { text: string; ok: boolean }[] = [];
  const docs = st.steps.filter(s => s.kind === 'doc' || s.kind === 'support');
  if (docs.length) out.push({ text: `Documents${docs.every(s => s.done) ? ' ✓' : ''}`, ok: docs.every(s => s.done) });
  for (const s of st.steps.filter(x => x.kind === 'sim' || x.kind === 'quiz')) {
    const label = s.kind === 'quiz' ? 'Quiz' : stepDef(p, s.step)?.sim?.short ?? s.step;
    out.push({ text: `${label} ${scoreLabel(s) || '·'}`.trim(), ok: s.done });
  }
  const prac = st.steps.find(s => s.kind === 'practice');
  if (prac) out.push({ text: `${plural(prac.count ?? 0, 'match', 'matchs')} ${p === 'otm' ? 'à la table' : 'arbitrés'}`, ok: prac.done });
  return out;
}

/** Une ligne « en cours » (référent, bureau) : « OTM · 3 sur 7 · pas à pas : 72 % (il faut 80 %) ». */
export function progressLine(p: ParcoursKey, st: Pick<ParcoursState, 'steps' | 'done' | 'total'>): string {
  const head = `${PARCOURS[p].name.replace(' ★★', ' ★★').replace(/ ★$/, '')} · ${st.done} sur ${st.total}`;
  const cur = nextStep(st);
  if (!cur) return head;
  if ((cur.kind === 'sim' || cur.kind === 'quiz') && (cur.best != null || cur.score != null)) return `${head} · ${stepShort(p, cur)} : ${scoreLabel(cur)} (${needLabel(cur)})`;
  if (cur.kind === 'practice') return `${head} · ${cur.count ?? 0} ${p === 'otm' ? 'match' : 'match arbitré'}${(cur.count ?? 0) > 1 ? 's' : ''} sur ${cur.threshold}`;
  const done = st.steps.filter(s => s.done && (s.kind === 'doc' || s.kind === 'support'));
  return done.length ? `${head} · ${done.map(s => (s.kind === 'doc' ? (p === 'otm' ? 'fiches lues' : 'document lu') : 'vidéo vue')).join(', ')}` : head;
}

// --- La validité : jusqu'au 30 juin (saison FFBB), remise à niveau en juin ------------------------------------------
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const parse = (d: string) => { const [y, m, j] = d.slice(0, 10).split('-').map(Number); return { y: y!, m: m!, j: j! }; };
/** « 30 juin 2027 », « 1er juin 2027 ». */
export function longDate(d: string): string { const x = parse(d); return `${x.j === 1 ? '1er' : x.j} ${MONTHS[x.m - 1]} ${x.y}`; }
/** « 31 oct. », « 1er nov. ». */
const SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
export function shortDate(d: string): string { const x = parse(d); return `${x.j === 1 ? '1er' : x.j} ${SHORT[x.m - 1]}`; }
/** La saison d'une date (« 2026-2027 ») : du 1er juillet au 30 juin. */
export function seasonOf(d: string): string { const x = parse(d); const y = x.m >= 7 ? x.y : x.y - 1; return `${y}-${y + 1}`; }
/** La fin de validité d'un badge validé ce jour-là : le 30 juin de la saison ; en juin, celui de la suivante (comme la base). */
export function validUntil(d: string): string { const x = parse(d); const y = x.m >= 7 ? x.y + 1 : x.y; return `${x.m === 6 ? y + 1 : y}-06-30`; }
/** La remise à niveau est-elle ouverte (en juin, ou badge à renouveler) ? */
export const renewalOpen = (today: string, cert: Pick<CertState, 'status'> | null): boolean => !!cert && (parse(today).m === 6 || cert.status === 'a_renouveler');

// --- Les supports du club : une vidéo YouTube (un lien), un PDF, un diaporama ----------------------------------------
/** L'identifiant d'une vidéo YouTube (watch, youtu.be, shorts, embed), sinon null. */
export function youtubeId(url: string | null | undefined): string | null {
  const m = (url ?? '').match(/^https:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/|youtube-nocookie\.com\/embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1]! : null;
}
/** Un lien de support accepté : https seulement ; une vidéo, YouTube seulement. */
export function supportUrlOk(kind: 'pdf' | 'slides' | 'video', url: string): boolean {
  if (!/^https:\/\/\S+$/.test(url) || url.length > 500) return false;
  return kind !== 'video' || !!youtubeId(url);
}
export const SUPPORT_LABEL: Record<'pdf' | 'slides' | 'video', { badge: string; cls: string; name: string }> = {
  pdf: { badge: 'PDF', cls: '', name: 'PDF' }, slides: { badge: 'DIA', cls: 'sl', name: 'Diaporama' }, video: { badge: 'YT', cls: 'yt', name: 'Vidéo YouTube' },
};

// --- Le message d'un simulateur (pont, public/formation/pont-*.js) ----------------------------------------------------
export interface SimMessage {
  source: 'arceau-formation'; kind: 'score'; sim: SimKey; mode: string; pct: number; score?: number; total?: number; ok?: number; err?: number; miss?: number; errs?: string[];
  /** D-145 (table) : toute la série avec la voix (true), son coupé ou jamais activé (false), pas de voix dans ce mode ou pas
   *  de voix française sur l'appareil (null). */
  sound?: boolean | null;
}
export interface FichesMessage { source: 'arceau-formation'; kind: 'fiches'; seen: number; passed: number; total: number }
export interface OpenSimMessage { source: 'arceau-formation'; kind: 'open-sim'; mode: string }
/** Un message reçu d'un simulateur, vérifié (forme, bornes) ; le reste est ignoré. */
export function readSimMessage(d: unknown): SimMessage | FichesMessage | OpenSimMessage | null {
  if (!d || typeof d !== 'object') return null;
  const m = d as Record<string, unknown>;
  if (m.source !== 'arceau-formation') return null;
  const int = (v: unknown, max: number) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max;
  if (m.kind === 'score' && (m.sim === 'table' || m.sim === 'gestes' || m.sim === 'placement') && typeof m.mode === 'string' && m.mode.length <= 12 && int(m.pct, 100)) {
    const errs = Array.isArray(m.errs) ? m.errs.filter((e): e is string => typeof e === 'string').slice(0, 5).map(e => e.slice(0, 200)) : undefined;
    return { source: 'arceau-formation', kind: 'score', sim: m.sim, mode: m.mode, pct: m.pct as number,
      ...(int(m.score, 1000) ? { score: m.score as number } : {}), ...(int(m.total, 1000) ? { total: m.total as number } : {}),
      ...(int(m.ok, 1000) ? { ok: m.ok as number } : {}), ...(int(m.err, 1000) ? { err: m.err as number } : {}), ...(int(m.miss, 1000) ? { miss: m.miss as number } : {}),
      ...(errs ? { errs } : {}), ...(typeof m.sound === 'boolean' || m.sound === null ? { sound: m.sound as boolean | null } : {}) };
  }
  if (m.kind === 'fiches' && int(m.seen, 100) && int(m.passed, 100) && int(m.total, 100)) return { source: 'arceau-formation', kind: 'fiches', seen: m.seen as number, passed: m.passed as number, total: m.total as number };
  if (m.kind === 'open-sim' && typeof m.mode === 'string' && /^[a-z0-9]{1,8}$/.test(m.mode)) return { source: 'arceau-formation', kind: 'open-sim', mode: m.mode };
  return null;
}
/** D-145 : un essai qui ne compte pas — l'étape se valide avec le son et la série a été jouée sans (son coupé ou jamais
 *  activé, alors que l'appareil a une voix française). L'app ne l'enregistre pas ; la base le refuse aussi. */
export const notCountedWithoutSound = (p: ParcoursKey, key: string, m: Pick<SimMessage, 'sound'>): boolean => stepDef(p, key)?.sound === true && m.sound === false;
export const SOUND_RULE = 'Pour valider, garde le son : comme en match, l’annonce est à l’oreille.';

/** L'étape d'un parcours qui correspond à un simulateur et à son mode. */
export function stepOfSim(p: ParcoursKey, sim: SimKey, mode: string): StepDef | undefined {
  return PARCOURS[p].steps.find(s => s.sim?.sim === sim && s.sim.value === mode);
}

// --- Les seuils réglables par le bureau (f9) ---------------------------------------------------------------------------
export const THRESHOLD_CHOICES: Record<string, number[]> = {
  'otm.table_pas': [60, 70, 80, 90], 'otm.table_arbitre': [60, 70, 80, 90], 'otm.table_chrono': [60, 70, 80, 90],
  'arb.gestes_rec': [70, 80, 90, 100], 'arb.gestes_num': [70, 80, 90, 100], 'arb.gestes_tab': [70, 80, 90, 100],
  'arb2.place_pl': [60, 70, 80, 90], 'arb2.place_ord': [60, 70, 80, 90], 'arb2.place_qz': [60, 70, 80, 90],
};
export const PRACTICE_CHOICES: Record<ParcoursKey, number[]> = { otm: [1, 2, 3, 4, 5], arb: [1, 2, 3, 4, 5], arb2: [3, 4, 5, 6, 8, 10] };
export const QUIZ_CHOICES = [7, 8, 9, 10];
/** Le résumé des seuils (f9) : « Table : 80, 80, 70 % · Gestes : 90, 80, 80 % » (pas à pas, avec l'arbitre, comme en match). */
export function thresholdsLine(th: Record<string, number>): string {
  const t = (k: string) => th[k] ?? PARCOURS[k.split('.')[0] as ParcoursKey].steps.find(s => s.key === k.split('.')[1])?.threshold ?? 0;
  return `Table : ${t('otm.table_pas')}, ${t('otm.table_arbitre')}, ${t('otm.table_chrono')} % · Gestes : ${t('arb.gestes_rec')}, ${t('arb.gestes_num')}, ${t('arb.gestes_tab')} %`;
}
